/**
 * The pending-proposal marker — the web's single owner of the component AND of the word
 * (Story 8.9, the AD-21 shape applied to the one vocabulary Epic 8 had not yet collapsed).
 *
 * `ProposedTag` was declared THREE times — `SiteDetail.tsx`, `DeviceDirectory.tsx`,
 * `VendorDirectory.tsx` — across eleven use sites, each under a docblock saying the markup
 * was the "same as" one of the others and nothing enforcing it. That is precisely the
 * shape Story 8.1 found for the band vocabulary and Story 8.5 for the device label, so it
 * gets the same treatment: one owner, and `proposed-tag.guard.test.ts` scanning the
 * sources rather than trusting this comment.
 *
 * **The text is the source of truth, never colour** (the epic's standing UX rule): the
 * pill carries the words, and `data-tone="neutral"` only tints them. A reader with no
 * colour perception, and a screen reader, both get the same fact.
 *
 * **Why the word is deliberately NOT in `device-label.ts`.** That module owns a device's
 * SUBJECT NAME — model plus revision — and is held byte-for-byte against the server
 * composer through `test-fixtures/device-label-cases.json` (AD-25). "Awaiting admin
 * verification" is a CURATION STATE, not part of how a device is named; folding it in
 * would make the two label owners disagree on data they are required to match, and
 * `device-label.guard.test.ts` would fail — correctly. So an `<option>`, which cannot host
 * an element, composes the two at the point of use via {@link proposedOptionSuffix}, and
 * every other site renders {@link ProposedTag}.
 *
 * The marker is rendered iff the record is still a proposal, so it disappears on its own
 * once an administrator verifies it (`proposed_by_company_id` cleared — the row keeps its
 * UUID) or merges it (row deleted) from the Requests inbox.
 */

/**
 * The ONE spelling of the marker. Every surface renders this string; nothing re-words it.
 * Kept byte-identical to the three copies it replaces, so no rendered output moved.
 */
export const PROPOSED_TAG_TEXT = 'Requested — awaiting admin';

/** The marker as a status pill, for every context that can host an element. */
export function ProposedTag(): React.JSX.Element {
  return (
    <span className="status-pill" data-tone="neutral" title={PROPOSED_TAG_TEXT}>
      <span className="status-pill__dot" aria-hidden="true" />
      {PROPOSED_TAG_TEXT}
    </span>
  );
}

/**
 * The marker as TEXT, for an `<option>` — the one context that cannot host an element
 * (Story 8.9). Returns a parenthesised suffix to append to the option's label, or the
 * empty string for a curated row, so a non-proposal renders exactly the text it rendered
 * before this story with no trailing separator left behind.
 *
 * Composed by the PICKER, never inside the device-label owner — see the module docblock.
 * Takes the raw `proposedByCompanyId` (or any truthy marker) rather than a boolean so the
 * call sites read `proposedOptionSuffix(d.proposedByCompanyId)` and cannot get the
 * polarity backwards on the way in.
 */
export function proposedOptionSuffix(proposed: string | boolean | null | undefined): string {
  return proposed ? ` (${PROPOSED_TAG_TEXT})` : '';
}
